import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  FaInfinity,
  FaPiggyBank,
  FaChartLine,
  FaUserShield,
  FaCheckCircle,
} from "react-icons/fa";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

// FAQ data + item
const FAQS = [
  {
    q: "Does permanent life always build cash value?",
    a: "Yes—unlike term, permanent policies include a cash value component. Growth method and speed depend on the specific product.",
  },
  {
    q: "Can I access my cash value?",
    a: "You can typically withdraw or borrow against it. Loans/withdrawals may reduce the death benefit and could have tax implications.",
  },
  {
    q: "Is permanent life good for estate planning?",
    a: "It can be. The lifelong coverage and potential tax advantages make it useful for legacy strategies—talk to our licensed agents and tax advisors.",
  },
];

function FAQItem({ q, a, i, openIndex, setOpenIndex }) {
  const isOpen = openIndex === i;
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpenIndex(isOpen ? null : i)}
        className="w-full text-left px-5 py-4 font-semibold flex items-center justify-between"
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${i}`}
      >
        <span className="pr-6">{q}</span>
        <span
          className={`ml-4 inline-block transform transition-transform duration-200 ${
            isOpen ? "rotate-45" : ""
          }`}
          aria-hidden="true"
        >
          +
        </span>
      </button>

      <motion.div
        id={`faq-panel-${i}`}
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        className="overflow-hidden px-5"
      >
        <div className="pb-5 text-gray-600">{a}</div>
      </motion.div>
    </div>
  );
}

// Modal components
function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-semibold border transition
        ${active ? "bg-[#002b5c] text-white border-[#002b5c]" : "bg-white text-[#002b5c] border-[#cfd8e3] hover:bg-gray-50"}`}
      type="button"
    >
      {children}
    </button>
  );
}

const modalVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, y: 10, scale: 0.98, transition: { duration: 0.15 } },
};

function ExploreTypesModal({ open, onClose, onJumpToTypes }) {
  const [tab, setTab] = useState("whole"); // "whole" | "ul" | "iul"
  const dialogRef = useRef(null);

  // Close on ESC, focus first interactive
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const first = dialogRef.current?.querySelector("button, a, [href], input, select, textarea");
    first?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Simple body scroll lock
  useEffect(() => {
    if (!open) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = overflow; };
  }, [open]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[80] bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="explore-types-title"
        ref={dialogRef}
        className="fixed inset-0 z-[81] flex items-center justify-center p-4"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <div
          className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b">
            <h3 id="explore-types-title" className="text-lg md:text-xl font-extrabold text-[#002b5c]">
              Explore Permanent Life Types
            </h3>
            <button
              onClick={onClose}
              type="button"
              aria-label="Close"
              className="rounded-lg px-3 py-1.5 border hover:bg-gray-50"
            >
              ✕
            </button>
          </div>

          <div className="p-5">
            <div className="flex flex-wrap gap-2 mb-5">
              <TabButton active={tab === "whole"} onClick={() => setTab("whole")}>Whole Life</TabButton>
              <TabButton active={tab === "ul"} onClick={() => setTab("ul")}>Universal Life (UL)</TabButton>
              <TabButton active={tab === "iul"} onClick={() => setTab("iul")}>Indexed UL (IUL)</TabButton>
            </div>

            <div className="space-y-4">
              {tab === "whole" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h4 className="font-bold text-[#002b5c]">Whole Life — guarantees & simplicity</h4>
                  <ul className="list-disc pl-5 text-gray-700 space-y-2 mt-2">
                    <li>Guaranteed premiums, death benefit, and cash value growth.</li>
                    <li>Potential dividends (with participating carriers).</li>
                    <li>Great for conservative, long‑term planning and legacy goals.</li>
                  </ul>
                </motion.div>
              )}
              {tab === "ul" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h4 className="font-bold text-[#002b5c]">Universal Life — flexibility & control</h4>
                  <ul className="list-disc pl-5 text-gray-700 space-y-2 mt-2">
                    <li>Adjustable premiums and death benefit (subject to policy limits).</li>
                    <li>Cash value credited with interest; performance depends on rates.</li>
                    <li>Good for changing incomes or targeted lifetime guarantees (GUL).</li>
                  </ul>
                </motion.div>
              )}
              {tab === "iul" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <h4 className="font-bold text-[#002b5c]">Indexed UL — growth potential with floors</h4>
                  <ul className="list-disc pl-5 text-gray-700 space-y-2 mt-2">
                    <li>Cash value tied to an index (caps/participation rates apply).</li>
                    <li>Downside protection via floors; no direct market investment.</li>
                    <li>Works for long horizons when you want upside potential.</li>
                  </ul>
                </motion.div>
              )}
            </div>

            <div className="flex flex-col md:flex-row gap-3 mt-6">
              <button
                onClick={() => { onClose(); onJumpToTypes(); }}
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[#002b5c] text-white font-semibold hover:opacity-90"
                type="button"
              >
                Go to Types section
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl border font-semibold hover:bg-gray-50"
              >
                Talk to an Agent
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function PermanentLife() {
  const [openIndex, setOpenIndex] = useState(null);
  const typesRef = useRef(null);
  const [highlight, setHighlight] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const handleExploreTypes = () => setModalOpen(true);

  const jumpToTypes = () => {
    typesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlight(true);
    setTimeout(() => setHighlight(false), 1600);
  };

  return (
    <>
      <Helmet>
        <title>Permanent Life Insurance | Your Choice Associates</title>
        <meta
          name="description"
          content="Lifelong protection with potential cash value growth. Learn the differences between whole, universal, and indexed universal life."
        />
        <link rel="canonical" href="https://yourchoiceassociates.org/permanent-life" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Permanent Life Insurance" />
        <meta
          property="og:description"
          content="Coverage that lasts a lifetime with living benefits like cash value you may access."
        />
        <meta property="og:url" content="https://yourchoiceassociates.org/permanent-life" />
        <meta property="og:site_name" content="Your Choice Associates" />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#002b5c] to-[#001b35] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <motion.div {...fade} className="space-y-5">
            <span className="inline-block text-xs font-semibold tracking-wider uppercase bg-white/10 px-3 py-1 rounded-full">
              Life Insurance
            </span>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
              Permanent Life Insurance
            </h1>
            <p className="text-white/90">
              Lifetime coverage with potential cash value growth you can borrow against. Ideal for
              legacy planning, long‑term needs, or business strategies.
            </p>
            <div className="flex gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center px-5 py-3 rounded-xl bg-white text-[#002b5c] font-semibold shadow hover:shadow-md transition"
              >
                Get a Quote
              </Link>
              <button
                onClick={handleExploreTypes}
                type="button"
                className="inline-flex items-center px-5 py-3 rounded-xl border border-white/30 text-white font-semibold hover:bg-white/10 transition"
              >
                Explore Types
              </button>
            </div>
          </motion.div>

          <motion.div {...fade} className="bg-white/5 rounded-2xl p-6 md:p-8">
            <ul className="space-y-4">
              {[
                { icon: <FaInfinity />, t: "Coverage that lasts your whole life" },
                { icon: <FaPiggyBank />, t: "Builds cash value over time" },
                { icon: <FaChartLine />, t: "Flexible premiums and benefits (varies by type)" },
                { icon: <FaUserShield />, t: "Estate & legacy planning advantages" },
              ].map((i, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="mt-1">{i.icon}</div>
                  <p>{i.t}</p>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Types */}
      <section
        id="types"
        ref={typesRef}
        className={`py-16 md:py-24 bg-white transition-shadow ${
          highlight ? "shadow-[0_0_0_4px_rgba(20,94,168,0.25)]" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-8">
            Types of Permanent Life Insurance
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Whole Life",
                desc:
                  "Guaranteed premiums, guaranteed death benefit, and guaranteed cash value growth. Simple and predictable.",
              },
              {
                title: "Universal Life (UL)",
                desc:
                  "Flexible premiums and death benefit options; cash value grows based on credited interest rates.",
              },
              {
                title: "Indexed UL (IUL)",
                desc:
                  "Cash value growth tied to a market index with caps and floors—upside potential with downside protection.",
              },
            ].map((card, i) => (
              <motion.div key={i} {...fade} className="bg-white rounded-2xl p-6 shadow-sm border">
                <h3 className="font-bold text-lg mb-2">{card.title}</h3>
                <p className="text-gray-600">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compare */}
      <section className="py-16 md:py-24 bg-[#f7fafc]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-8">
            When Permanent Life makes sense
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div {...fade} className="bg-white rounded-2xl p-6 border">
              <h3 className="font-semibold text-lg mb-3">Common goals</h3>
              <ul className="list-disc pl-5 text-gray-700 space-y-2">
                <li>Leave a guaranteed legacy or cover final expenses</li>
                <li>Supplement retirement or access cash value for opportunities</li>
                <li>Business strategies (key person, buy‑sell funding)</li>
                <li>Estate planning and potential tax advantages</li>
              </ul>
            </motion.div>
            <motion.div {...fade} className="bg-white rounded-2xl p-6 border">
              <h3 className="font-semibold text-lg mb-3">Considerations</h3>
              <ul className="list-disc pl-5 text-gray-700 space-y-2">
                <li>Higher premiums than term for the same death benefit</li>
                <li>Cash value growth depends on product type and funding</li>
                <li>Policy loans/withdrawals may reduce death benefit</li>
              </ul>
            </motion.div>
          </div>
          <div className="mt-8">
            <Link
              to="/term-life"
              className="inline-flex items-center gap-2 text-[#002b5c] font-semibold hover:underline"
            >
              Compare with Term Life →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs (accordion) */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-8">
            Frequently Asked Questions
          </motion.h2>

          <div className="space-y-4">
            {FAQS.map((item, i) => (
              <FAQItem
                key={i}
                i={i}
                q={item.q}
                a={item.a}
                openIndex={openIndex}
                setOpenIndex={setOpenIndex}
              />
            ))}
          </div>

          <div className="mt-8">
            <Link
              to="/start"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#e31c3d] text-white font-semibold shadow hover:shadow-md transition"
            >
              Talk with a Licensed Agent
            </Link>
          </div>
        </div>
      </section>

      {/* Trust/CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-[#002b5c] font-semibold">
            <FaCheckCircle /> Experienced guidance • Personalized recommendations • No pressure
          </div>
        </div>
      </section>

      <Footer />

      {/* Explore Types Modal */}
      <ExploreTypesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onJumpToTypes={jumpToTypes}
      />
    </>
  );
}
